import './styles.scss';

import { DeleteOutlined } from '@ant-design/icons';
import { WlInvoicesMenuSidebarIcon } from '@components/layout/sidebar/icons/white_label/invoices-menu-icon';
import { WlPayoutMenuSideBarIcon } from '@components/layout/sidebar/icons/white_label/payout-menu-icon';
import { CustomSearch } from '@components/modules/custom-search';
import { FilterStatus } from '@components/modules/filter-status';
import { CustomPagination } from '@components/modules/pagination';
import { PAGE_SIZE, SA_ROLE } from '@constants/index.constant';
import { PERMISSIONS } from '@constants/permission';
import { UserTypeEnum } from '@enums/user-type.enum';
import { IMarketplace } from '@interfaces/marketplace';
import { IPartner } from '@interfaces/partner';
import { ISubscription } from '@interfaces/subscription';
import { IUser } from '@interfaces/user';
import {
    getDefaultSortOrder,
    mapAntdSorterToCrudSorting,
    ShowButton,
    useModal,
} from '@refinedev/antd';
import {
    LogicalFilter,
    useCreate,
    useCustom,
    useGetIdentity,
    usePermissions,
    useTable,
    useUpdate,
} from '@refinedev/core';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { convertPrice } from '@utils/resource';
import {
    Button,
    Dropdown,
    Menu,
    Modal,
    Popconfirm,
    Select,
    Spin,
    Table,
    TablePaginationConfig,
    Tooltip,
} from 'antd';
import { SorterResult } from 'antd/lib/table/interface';
import { getEnvConfig } from 'getEnvConfig';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { formatAppSubscriptionPeriodEnd, RECURRING_SUBSCRIPTION_SUBTRACT_DAY } from 'utils/date';

import { SelectPartnerCrm } from '../../../components/internal-crm/select-partner';
import PaymentForm from '../../../components/setting/payment-method/payment-form';
import { DataProviderNameEnum } from '../../../dataProvider';
import { PartnerTypeEnum } from '../../../enums/partner-type.enum';
import { SubscriptionInvoiceStatus } from '../../../enums/subscription-invoice-status.enum';

export const SubscriptionCrmIndex: React.FC = () => {
    const { mutate } = useUpdate();
    const { data: user } = useGetIdentity<IUser.IUserDto>();

    const { t: translate } = useTranslation(['contact', 'common', 'quote', 'marketplace']);
    const { data: permissionsData } = usePermissions<string[]>();
    const [partnerId, setPartnerId] = useState<string>();
    const [contactId, setContactId] = useState<string>();
    const [contact, setContact] = useState<any[]>([]);
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const { modalProps, show, close } = useModal({
        modalProps: {
            onCancel: () => {
                setClientSecret(null);
            },
        },
    });

    const apiUrl = getEnvConfig.INTERNAL_CRM_API_URL;
    const dataProviderName = DataProviderNameEnum.INTERNAL_CRM;

    const stripePromise = loadStripe(getEnvConfig.STRIPE_PUBLIC_KEY);

    const { mutate: createPaymentIntent } = useCreate();
    const isSysAdmin = user?.roles && user?.roles.indexOf(SA_ROLE) !== -1;
    const handleUpdatePayment = (record: ISubscription.SubscriptionInvoiceList) => {
        show();
        createPaymentIntent(
            {
                dataProviderName: dataProviderName,
                resource: `v1/sale-subscription/payment/${record?.id}`,
                values: {},
                errorNotification: false,
                successNotification: false,
            },
            {
                onError: (error) => {
                    // An error happened!
                    console.error(error);
                },
                onSuccess: (res) => {
                    const secret = res?.data?.data as string;
                    setClientSecret(secret);
                },
            },
        );
    };

    const onPaymentIntentSuccess = () => {
        // close modal reload subscriptions
        close();
        setClientSecret(null);

        //update subscription
    };

    const { data: contactsData, refetch: contactRefetch } = useCustom<IPartner.IPartnerInfo[]>({
        url: `${apiUrl}/v1/contacts/list`,
        method: 'get',
        config: {
            filters: [
                {
                    field: 'partnerId',
                    operator: 'eq',
                    value: partnerId,
                },
            ],
        },
        queryOptions: {
            enabled: false,
        },
    });

    useEffect(() => {
        if (partnerId) {
            contactRefetch();
        }
    }, [partnerId]);

    useEffect(() => {
        if (user && !partnerId) {
            setPartnerId(user.id);
        }
    }, []);
    const {
        setFilters,
        filters,
        setSorters,
        sorters,
        tableQueryResult,
        setCurrent,
        current,
        setPageSize,
        pageSize,
    } = useTable<ISubscription.SubscriptionInvoiceList>({
        dataProviderName,
        resource: 'v1/application-subscriptions/invoices',
        sorters: {
            initial: [
                {
                    field: 'createdAt',
                    order: 'desc',
                },
            ],
        },
        pagination: {
            pageSize: PAGE_SIZE,
        },
        syncWithLocation: true,
    });

    const menu = (id: string, record: ISubscription.SubscriptionInvoiceList) => {
        return (
            <Menu mode="vertical">
                {(permissionsData || []).includes(PERMISSIONS.WL_INVOICE_SHOW) ? (
                    <Menu.Item key="0" style={{ padding: 0 }}>
                        <Link to={`/sale/invoices/show/${record.invoiceId}`}>
                            <Button
                                icon={<WlInvoicesMenuSidebarIcon />}
                                style={{
                                    padding: '5px 16px 5px 12px',
                                    margin: 0,
                                }}
                                className="icon-action-subscription-list"
                            >
                                {translate('subscriptions.action.viewInvoice', {
                                    ns: 'common',
                                })}
                            </Button>
                        </Link>
                    </Menu.Item>
                ) : null}
                {(permissionsData || []).includes(PERMISSIONS.WL_RECURRING_INVOICE_SHOW) ? (
                    <Menu.Item
                        key="1"
                        icon={
                            <ShowButton
                                recordItemId={record.id}
                                children={translate('subscriptions.action.viewBillingHistory', {
                                    ns: 'common',
                                })}
                                className="icon-action-subscription-list"
                                icon={<WlPayoutMenuSideBarIcon />}
                                style={{
                                    padding: '5px 24px 5px 12px',
                                    margin: 0,
                                }}
                            />
                        }
                        style={{ padding: 0 }}
                    ></Menu.Item>
                ) : null}
                {record.status === SubscriptionInvoiceStatus.Open &&
                    (permissionsData || [])?.indexOf(PERMISSIONS.WL_RECURRING_INVOICE_EDIT) >=
                        0 && (
                        <Menu.Item
                            key="2"
                            icon={
                                <Popconfirm
                                    key="delete"
                                    okText={'Sure'}
                                    cancelText={'Cancel'}
                                    title={'Are you sure?'}
                                    onConfirm={(): void => {
                                        mutate({
                                            dataProviderName,
                                            resource: 'v1/sale-subscription/cancel',
                                            id: record.subscriptionId,
                                            values: '',
                                            successNotification: (data) => {
                                                tableQueryResult.refetch();
                                                return {
                                                    message: translate(
                                                        'subscriptions.cancel_subscription_successfully',
                                                        {
                                                            ns: 'common',
                                                        },
                                                    ),
                                                    type: 'success',
                                                };
                                            },
                                            errorNotification: (data) => {
                                                return {
                                                    message:
                                                        'subscriptions.cancel_subscription_fail',
                                                    type: 'error',
                                                };
                                            },
                                        });
                                    }}
                                >
                                    <Button
                                        icon={<DeleteOutlined className="mr-2" />}
                                        style={{
                                            padding: '5px 16px 5px 12px',
                                            margin: 0,
                                        }}
                                    >
                                        {translate('subscriptions.action.cancel', {
                                            ns: 'common',
                                        })}
                                    </Button>
                                </Popconfirm>
                            }
                            style={{ padding: 0 }}
                        ></Menu.Item>
                    )}
                {record.status === SubscriptionInvoiceStatus.Uncollectible ? (
                    <Menu.Item
                        style={{ padding: 0 }}
                        key="3"
                        icon={
                            <Button
                                onClick={() => handleUpdatePayment(record)}
                                className="icon-action-subscription-list"
                                icon={
                                    <img
                                        src="/images/icons/edit.svg"
                                        alt="edit"
                                        className="cursor-pointer mr-2"
                                    />
                                }
                                style={{
                                    padding: '5px 24px 5px 12px',
                                    margin: 0,
                                }}
                            >
                                <span>
                                    {translate('subscriptions.action.update_payment', {
                                        ns: 'common',
                                    })}
                                </span>
                            </Button>
                        }
                    ></Menu.Item>
                ) : null}
            </Menu>
        );
    };

    const onChangeFilter = (event: any) => {
        setFilters((prev) => {
            const newFilter = prev;
            const index = newFilter.findIndex((item) => (item as LogicalFilter).field === 'filter');
            if (index !== -1) {
                (newFilter[index] as LogicalFilter).value = event.target.value;
            } else {
                newFilter.push({
                    field: 'filter',
                    operator: 'eq',
                    value: event.target.value,
                });
            }

            return newFilter;
        });

        setCurrent(1);
    };

    const onChangeStatus = (status: string) => {
        setFilters((prev) => {
            const newFilter = prev;
            const index = newFilter.findIndex((item) => (item as LogicalFilter).field === 'status');
            if (index !== -1) {
                (newFilter[index] as LogicalFilter).value = status;
            } else {
                newFilter.push({
                    field: 'status',
                    operator: 'eq',
                    value: status,
                });
            }
            return newFilter;
        });

        setCurrent(1);
    };

    const onChangeTable = (
        pagination: TablePaginationConfig,
        tableFilters: Record<
            string,
            (string | number | boolean) | (string | number | boolean)[] | null
        >,
        sorter: SorterResult<any> | SorterResult<any>[],
    ) => {
        if (sorter && Object.keys(sorter).length > 0) {
            // Map Antd:Sorter -> refine:CrudSorting
            const crudSorting = mapAntdSorterToCrudSorting(sorter);
            setSorters(crudSorting);
        }
    };

    const onClearPartner = () => {
        setPartnerId(user?.id);
        onClearContact();
        setFilters([
            {
                field: 'partnerId',
                operator: 'eq',
                value: undefined,
            },
        ]);
    };

    const onClearContact = () => {
        setFilters([
            {
                field: 'contactId',
                operator: 'eq',
                value: undefined,
            },
        ]);
    };

    const handleChangeFilter = (partnerId: string) => {
        setPartnerId(partnerId);
        setFilters([
            {
                field: 'partnerId',
                operator: 'eq',
                value: partnerId,
            },
        ]);
        setFilters([
            {
                field: 'contactId',
                operator: 'eq',
                value: undefined,
            },
        ]);
    };

    useEffect(() => {
        setContactId(undefined);
        if (partnerId) {
            setContact(
                (contactsData?.data || []).map((contact: any) => {
                    const name = `${contact.firstName} ${contact.lastName}`;
                    return {
                        label: name.length > 20 ? name.substring(0, 20) + '...' : name,
                        value: contact.id,
                    };
                }),
            );
        }
    }, [partnerId, contactsData?.data]);

    const invoiceStatuses = [SubscriptionInvoiceStatus.Open, SubscriptionInvoiceStatus.Paid];
    const LIST_STATUS_FILTER = invoiceStatuses.map((status) => {
        return {
            label: translate(`subscriptions.applicationSubscriptionInvoiceStatus.${status}`, {
                ns: 'common',
            }),
            value: status,
        };
    });
    return (
        <>
            <section className="item-list-container subscription-page-wrapper ">
                <div className="list-header">
                    <div className="filter-wrapper">
                        <CustomSearch
                            placeholder={translate('quotes.fields.search.invoce_placeholder', {
                                ns: 'quote',
                            })}
                            className={'search-item custom-width '}
                            onChange={(event) => onChangeFilter(event)}
                            defaultValue={
                                filters?.find((f) => (f as LogicalFilter).field === 'filter')?.value
                            }
                            allowClear={true}
                        />
                        <FilterStatus
                            placeholder={translate('quotes.fields.search.status_placeholder', {
                                ns: 'quote',
                            })}
                            listStatus={LIST_STATUS_FILTER}
                            className="search-status"
                            defaultValue={
                                filters?.find((f) => (f as LogicalFilter).field === 'status')?.value
                            }
                            onChangeStatus={(status: string) => onChangeStatus(status)}
                        />
                        {isSysAdmin && (
                            <>
                                <SelectPartnerCrm
                                    type={PartnerTypeEnum.RESELLER}
                                    key="1"
                                    onChange={(option, _) => {
                                        handleChangeFilter(option);
                                    }}
                                    allowClear
                                    placeholder={translate(
                                        'contacts.fields.search.placeholder_reseller',
                                        {
                                            ns: 'contact',
                                        },
                                    )}
                                    getSelectedPartner={(partner) => {
                                        if (partner) {
                                            handleChangeFilter(partner.id ?? '');
                                        }
                                    }}
                                    className="search-item custom-width"
                                    clearPartner={onClearPartner}
                                />

                                <Select
                                    className="filter-status-component"
                                    showSearch={false}
                                    allowClear
                                    onChange={(option, _) => {
                                        setContactId(option);
                                        setFilters([
                                            {
                                                field: 'contactId',
                                                operator: 'eq',
                                                value: option,
                                            },
                                        ]);
                                    }}
                                    placeholder={translate(
                                        'contacts.fields.search.placeholder_contact',
                                        {
                                            ns: 'contact',
                                        },
                                    )}
                                    value={contactId}
                                    options={contact}
                                    onClear={onClearContact}
                                />
                            </>
                        )}
                    </div>
                </div>
                <div className="overflow-hidden">
                    <div className="list-content table-wrapper">
                        <Table
                            dataSource={tableQueryResult.data?.data}
                            loading={tableQueryResult.isFetching}
                            onChange={onChangeTable}
                            pagination={false}
                            tableLayout="fixed"
                            scroll={{ x: '900px' }}
                            rowKey="id"
                        >
                            <Table.Column
                                sorter
                                defaultSortOrder={getDefaultSortOrder('incrementId', sorters)}
                                title={
                                    <>
                                        {translate('subscriptions.invoiceId', {
                                            ns: 'common',
                                        })}
                                    </>
                                }
                                dataIndex="incrementId"
                                key="incrementId"
                                width={140}
                                render={(
                                    incrementId,
                                    record: ISubscription.SubscriptionInvoiceList,
                                ) => {
                                    return (
                                        <p className="table-tbody-text">
                                            {record?.invoiceIncrementId}
                                        </p>
                                    );
                                }}
                            />
                            <Table.Column
                                title={
                                    <>
                                        {translate('subscriptions.statusTitle', {
                                            ns: 'common',
                                        })}
                                    </>
                                }
                                dataIndex="status"
                                key="status"
                                width={120}
                                render={(status, _) => {
                                    return (
                                        <p className="table-tbody-text">
                                            {translate(
                                                'subscriptions.applicationSubscriptionInvoiceStatus.' +
                                                    status.toString(),
                                                {
                                                    ns: 'common',
                                                },
                                            )}
                                        </p>
                                    );
                                }}
                            />
                            <Table.Column
                                title={
                                    <>
                                        {translate('subscriptions.productName', {
                                            ns: 'common',
                                        })}
                                    </>
                                }
                                dataIndex="applicationTitle"
                                key="productName"
                                width={300}
                                render={(applicationTitle, _) => {
                                    return (
                                        <p className="table-tbody-text">
                                            <Tooltip title={applicationTitle}>
                                                {applicationTitle}
                                            </Tooltip>
                                        </p>
                                    );
                                }}
                            />
                            <Table.Column
                                title={
                                    <>
                                        {translate('subscriptions.nextInvoiceDate', {
                                            ns: 'common',
                                        })}
                                    </>
                                }
                                dataIndex="periodEnd"
                                key="nextInvoiceDate"
                                width={200}
                                render={(periodEnd, record: IMarketplace.ISubscriptionInvoice) => {
                                    return (
                                        <p className="table-tbody-text">
                                            {/* System auto add extra 1 day to period end of recurring subscription 
                                                So need to subtract 1 day when showing in UI for correct data
                                            */}
                                            {formatAppSubscriptionPeriodEnd(
                                                RECURRING_SUBSCRIPTION_SUBTRACT_DAY,
                                                periodEnd as Date,
                                                record,
                                            )}
                                        </p>
                                    );
                                }}
                            />
                            <Table.Column
                                title={
                                    <>
                                        {translate('subscriptions.price', {
                                            ns: 'common',
                                        })}
                                    </>
                                }
                                dataIndex="amount"
                                key="price"
                                width={150}
                                render={(amount, _) => {
                                    return (
                                        <p className="table-tbody-text">{convertPrice(amount)}</p>
                                    );
                                }}
                            />
                            <Table.Column
                                title={
                                    <>
                                        {translate('subscriptions.frequencyTitle', {
                                            ns: 'common',
                                        })}
                                    </>
                                }
                                dataIndex="frequency"
                                key="frequency"
                                width={150}
                                render={(frequency, _) => {
                                    const transFrequency = translate(
                                        'subscriptions.frequency.' + frequency?.toString(),
                                        {
                                            ns: 'marketplace',
                                        },
                                    );

                                    return <p className="table-tbody-text">{transFrequency}</p>;
                                }}
                            />

                            <Table.Column
                                title={
                                    <>
                                        {translate('subscriptions.tenant', {
                                            ns: 'common',
                                        })}
                                    </>
                                }
                                dataIndex="tenantName"
                                key="tenantName"
                                width={150}
                                render={(tenantName, _) => {
                                    return <p className="table-tbody-text">{tenantName}</p>;
                                }}
                            />
                            {isSysAdmin && (
                                <Table.Column
                                    title={
                                        <>
                                            {translate('subscriptions.reseller', {
                                                ns: 'common',
                                            })}
                                        </>
                                    }
                                    dataIndex="resellerName"
                                    key="resellerName"
                                    width={150}
                                    render={(resellerName, _) => {
                                        return <p className="table-tbody-text">{resellerName}</p>;
                                    }}
                                />
                            )}
                            <Table.Column
                                dataIndex="id"
                                key="action"
                                width={100}
                                fixed="right"
                                render={(id, record: ISubscription.SubscriptionInvoiceList) => (
                                    <div className="flex justify-between items-center">
                                        {(permissionsData || []).some((p) =>
                                            [
                                                PERMISSIONS.WL_INVOICE_SHOW,
                                                PERMISSIONS.WL_RECURRING_INVOICE_SHOW,
                                                PERMISSIONS.WL_RECURRING_INVOICE_EDIT,
                                            ].includes(p),
                                        ) ? (
                                            <Dropdown
                                                overlay={menu(id, record)}
                                                placement="bottomRight"
                                                className="w-8 ml-auto"
                                                arrow
                                                overlayClassName="contact-dropdown-container"
                                                trigger={['click']}
                                            >
                                                <div className="">
                                                    <img
                                                        src="/images/icons/dots-vertical.svg"
                                                        alt="more"
                                                        className="cursor-pointer"
                                                    />
                                                </div>
                                            </Dropdown>
                                        ) : null}
                                    </div>
                                )}
                            />
                        </Table>
                    </div>
                </div>
                <div className="pagination-container pt-3 pb-4 px-6">
                    <CustomPagination
                        pageSize={pageSize}
                        total={tableQueryResult?.data?.total}
                        current={current}
                        onChange={(value: number, size: number) => {
                            setCurrent(value);
                            setPageSize(size);
                        }}
                    />
                </div>
            </section>

            <Modal
                {...modalProps}
                title="Payment"
                maskClosable={false}
                width={650}
                wrapClassName="payment-modal"
                footer={false}
            >
                {clientSecret ? (
                    <Elements
                        stripe={stripePromise}
                        options={{
                            clientSecret: clientSecret,
                        }}
                    >
                        <PaymentForm onSuccess={onPaymentIntentSuccess} />
                    </Elements>
                ) : null}
                {!clientSecret ? (
                    <div className="mt-2 mb-2">
                        <Spin spinning={true} />
                    </div>
                ) : null}
            </Modal>
        </>
    );
};
